// @flow
import * as React from 'react';
import classNames from 'classnames/bind';

import cs from './styles.pcss';
import Lightbox from '../../../basic-components/lightbox';
import Button from '../../../basic-components/button';
import Icon from '../../../basic-components/icon';
import FlagIcon from '../../../basic-components/flag-icon';
import t from './locale';

const cx = classNames.bind(cs);

type Props = {
  changedProductsCount: number,
  showcasesToActivateCount: number,
  showcaseData: Array<{ name: string, code: string }>,
  onConfirm: () => void,
  onCancel: () => void,
  loading: boolean,
  opened: boolean,
};

export default function ApplyChangesConfirmation({
  changedProductsCount,
  showcasesToActivateCount,
  showcaseData,
  onConfirm,
  onCancel,
  loading,
  opened,
}: Props): React.Node {
  const renderFooter = React.useCallback(
    (closeOverlay: () => Promise<void>) => (
      <div className={cx('footer')}>
        <Button
          mode="secondary"
          onClick={async () => {
            await closeOverlay();
            onCancel();
          }}
          disabled={loading}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={() => {
            onConfirm();
          }}
          loading={loading}
          mode="main"
        >
          {t('confirm')}
        </Button>
      </div>
    ),
    [loading, onConfirm, onCancel]
  );

  return (
    <Lightbox
      opened={opened}
      small
      header={t('title')}
      onClose={onCancel}
      footer={renderFooter}
      lightboxName="apply-changes-confirmation"
    >
      <div className={cx('content')}>
        <div className={cx('icon-wrapper')}>
          <Icon name="exclamationCircle" className={cx('icon')} />
        </div>

        <div className={cx('message')}>
          <p className={cx('primary-message')}>
            {t('message.products', { count: changedProductsCount })}
          </p>

          {showcasesToActivateCount > 0 && (
            <div className={cx('showcase-activation')}>
              <p className={cx('secondary-message')}>
                {t('message.showcases', { count: showcasesToActivateCount })}
              </p>
              <ul className={cx('showcase-list')}>
                {showcaseData.map((showcase) => (
                  <li key={showcase.name} className={cx('showcase-item')}>
                    {showcase.code && (
                      <FlagIcon
                        name={showcase.code}
                        size={16}
                        className={cx('flag-icon')}
                      />
                    )}
                    <span className={cx('showcase-name')}>{showcase.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className={cx('confirmation-message')}>{t('message.continue')}</p>
        </div>
      </div>
    </Lightbox>
  );
}
